import { useContext, useEffect } from "react";

import { useLocation } from "react-router";

import AuthContext from "context/auth/authContext";
import LanguageContext from "context/language/languageContext";

import { getEnvironment, isAnalyticsCookieAccepted } from "../../functions";
import {
    getPageViewParameters,
    initializeGoogleAnalytics,
    setGoogleAnalyticsUserId,
    setGoogleAnalyticsUserProperties,
    trackGoogleAnalyticsEvent
} from "./functions";

const measurementId: string = (
    import.meta.env.VITE_GA_MEASUREMENT_ID ?? ""
).trim();

export const useGoogleAnalytics = () => {
    const { language } = useContext(LanguageContext);
    const { cookieConsent, user } = useContext(AuthContext);

    const { pathname, search } = useLocation();
    const { isProductionEnv, env } = getEnvironment();

    const canTrack =
        Boolean(measurementId) &&
        isProductionEnv &&
        isAnalyticsCookieAccepted(cookieConsent);

    useEffect(() => {
        if (!canTrack) return;

        initializeGoogleAnalytics(measurementId);
    }, [canTrack]);

    useEffect(() => {
        if (!canTrack) return;

        const pageViewParameters = getPageViewParameters(pathname, search);
        trackGoogleAnalyticsEvent("page_view", pageViewParameters);
    }, [canTrack, pathname, search]);

    useEffect(() => {
        if (!canTrack) return;

        setGoogleAnalyticsUserId(
            measurementId,
            user ? String(user.id) : undefined
        );

        if (!user) return;

        setGoogleAnalyticsUserProperties({
            company_id: String(user.company_id),
            access_level: user.access_level,
            company_level: user.company_level,
            company_type: user.company_type,
            user_language: language,
            app_environment: env
        });
    }, [canTrack, user, language, env]);
};
